import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MdMoreVert } from "react-icons/md";
import styled from "styled-components/macro";

// Kebab (3-dots) row-actions menu (AC-15377). Always-visible trigger; the menu
// is portaled to document.body so the table's overflow:auto scroll container
// can't clip it. Closes on outside-click, Escape, and scroll/resize.
// Pass menu rows as children (e.g. <MenuItem>…</MenuItem>).

const Trigger = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  padding: 0.25rem;
  border-radius: 4px;
  color: rgba(0, 0, 0, 0.6);
  &:hover {
    background: #f0f0f0;
  }
`;

const Menu = styled.div`
  position: fixed;
  z-index: 9999;
  min-width: 160px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.18);
  padding: 0.25rem;
`;

export const MenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.4rem 0.6rem;
  font-size: 0.85rem;
  text-align: left;
  border-radius: 4px;
  color: ${(p) => (p.danger ? "#dc2626" : "inherit")};
  &:hover:not(:disabled) {
    background: #f5f5f5;
  }
  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

const RowActionsMenu = ({ children, ariaLabel = "Row actions" }) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const positionMenu = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setCoords({
      top: rect.bottom + 4,
      right: Math.max(8, window.innerWidth - rect.right),
    });
  };

  useEffect(() => {
    if (!open) return undefined;
    const onMouseDown = (e) => {
      if (
        triggerRef.current?.contains(e.target) ||
        menuRef.current?.contains(e.target)
      ) {
        return;
      }
      setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    const close = () => setOpen(false);
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  return (
    <>
      <Trigger
        ref={triggerRef}
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="menu"
        onClick={() =>
          setOpen((o) => {
            const next = !o;
            if (next) positionMenu();
            return next;
          })
        }
      >
        <MdMoreVert />
      </Trigger>
      {open && coords
        ? createPortal(
            <Menu
              ref={menuRef}
              role="menu"
              style={{ top: coords.top, right: coords.right }}
              // Close after a menu item is clicked (the item handler fires first
              // via bubbling).
              onClick={() => setOpen(false)}
            >
              {children}
            </Menu>,
            document.body
          )
        : null}
    </>
  );
};

export default RowActionsMenu;
